// Cognito's sign-in challenge session lives only AuthSessionValidity minutes
// (an app-client setting, 3–15) after the password step. When it lapses,
// Cognito rejects the next confirmSignIn with NotAuthorizedException even
// though what the user typed — a TOTP code, a new password — is still valid.
// Amplify keeps the dead session in memory, so every retry fails the same way.

export const isExpiredSignInSession = (error) =>
  (error?.name || error?.code) === 'NotAuthorizedException' &&
  /session/i.test(error?.message || '');

/**
 * confirmSignIn, but if the challenge session has expired, redo the password
 * step with `credentials` to get a fresh session and resubmit the same
 * response once — provided Cognito lands on `expectedStep` again.
 *
 * - Restart fails (password changed elsewhere, account disabled, network):
 *   the original expiry is rethrown, so the UI sends the user back to the
 *   login form, which then shows the real reason.
 * - Restart completes sign-in by itself (the account no longer needs this
 *   challenge, e.g. an admin removed MFA): returned as success with
 *   `signedInOnRestart: true`, since the challenge was never answered.
 * - Restart lands on any other step: the original expiry is rethrown.
 *
 * signIn/confirmSignIn are passed in (from aws-amplify/auth) to keep this
 * module free of Amplify imports.
 */
export async function confirmSignInWithFreshSession({
  challengeResponse,
  expectedStep,
  credentials,
  signIn,
  confirmSignIn,
}) {
  try {
    return await confirmSignIn({ challengeResponse });
  } catch (error) {
    if (!isExpiredSignInSession(error) || !credentials) throw error;

    console.warn('Sign-in challenge session expired; restarting sign-in and resubmitting');
    let restart;
    try {
      restart = await signIn(credentials);
    } catch (restartError) {
      console.warn('Restarting sign-in failed:', restartError);
      throw error;
    }
    if (restart?.isSignedIn || restart?.nextStep?.signInStep === 'DONE') {
      return { ...restart, signedInOnRestart: true };
    }
    if (restart?.nextStep?.signInStep !== expectedStep) throw error;

    return confirmSignIn({ challengeResponse });
  }
}
